import type { AlertSeverity, AlertSignal, DbEventKind, StorageKind, TxType } from '@apm/contracts';

/**
 * 화면 용어집 (19회차, 콘솔 검토 C-19·C-20. 19회차 보완에서 말투를 개발 문서 기준으로 조정).
 *
 * 이 화면을 보는 사람은 관제 담당자이고, 1년차 개발자가 아는 용어까지는 그대로 쓴다
 * (2026-09-08 사용자 피드백 2): API·응답(response)·요청 파라미터·필드·토큰·경로·데이터 경로·
 * 상태 코드·JSON/CSV·인스턴스·프로세스·세션·트랜잭션·쿼리·커넥션 풀.
 *
 * 계측·저장 내부 용어는 여전히 화면에 내지 않는다: `span`·`trace`·`p99`·`p95`·`힙`·`GC`·
 * `이벤트루프`·`핫`/`콜드`·`스냅샷`·`워처`·`발화`·`임계값`·`플러시`·`버킷`·`OTLP`·`SQLSTATE`·
 * `traceparent`·`tenant`. 파일 형식 이름(NDJSON·Parquet)은 저장 층 이름으로는 쓰지 않고
 * 설명 문장과 툴팁에만 둔다.
 *
 * 문구를 한 파일에 모으는 이유는 같은 것을 화면마다 다르게 부르는 일을 막기 위함이다.
 * **계약(`@apm/contracts`)의 라벨은 고치지 않는다.** 그쪽은 서버·외부 연동도 쓰는 값이라
 * 화면 사정으로 바꾸면 범위가 넘친다. 대신 여기서 화면용 이름을 따로 둔다.
 */

// ---- 탭 ----

/** 위 줄의 메뉴 여섯 (22-B회차, 2026-09-08 사용자 확정 5). 성격별로 묶었다 */
export const TAB_LABEL = {
  live: '실시간',
  requests: '요청',
  stats: '통계',
  diagnose: '진단',
  alerts: '알림',
  admin: '관리',
} as const;

/** 진단·관리 아래의 작은 탭. 주소는 `#/diagnose/errors`처럼 두 단계다 */
export const SUB_TAB_LABEL = {
  errors: '오류',
  db: 'DB 감시',
  cpu: 'CPU 기록',
  storage: '저장소',
  external: '외부 연동',
  server: '관제 서버',
} as const;

// ---- 트랜잭션 타입 ----

/**
 * 타입의 화면 이름. `http`·`sse`는 그대로 두면 뜻이 안 통한다.
 * `MCP`는 제품 이름이라 남기고 무엇인지만 덧붙인다.
 *
 * `slow`는 26회차에 계약에만 늘었다. 이 값을 만드는 곳이 아직 없어 화면에 나오지 않지만,
 * 이름·색·모양이 없으면 타입 검사가 걸린다. 제대로 된 자리는 27회차다
 */
export const TX_TYPE_LABEL: Record<TxType, string> = {
  http: '웹 요청',
  sse: '실시간 연결',
  mcp: 'MCP 도구',
  worker: '정기 작업',
  pdf: 'PDF 만들기',
  slow: '오래 걸리는 요청',
};

/** 고르는 칸에 쓰는 목록. 순서는 건수가 많은 쪽부터 */
export const TX_TYPE_OPTIONS: readonly { value: TxType; label: string }[] = [
  { value: 'http', label: TX_TYPE_LABEL.http },
  { value: 'sse', label: TX_TYPE_LABEL.sse },
  { value: 'mcp', label: TX_TYPE_LABEL.mcp },
  { value: 'worker', label: TX_TYPE_LABEL.worker },
  { value: 'pdf', label: TX_TYPE_LABEL.pdf },
  { value: 'slow', label: TX_TYPE_LABEL.slow },
];

// ---- 알림 ----

/** 계약의 `AlertState`를 화면 말로. "발화"는 소방 용어지 관제 용어가 아니다 */
export const ALERT_STATE_LABEL = {
  firing: '경보 중',
  resolved: '해제됨',
  /** 그 순간의 통보. 경보로 남지 않는다 (24회차) */
  notice: '알림',
} as const;

/** 알림 한 건에 매겨진 심각도 */
export const ALERT_SEVERITY_LABEL: Record<AlertSeverity, string> = {
  critical: '심각',
  warning: '경고',
};

/** 담당자가 받을 알림의 최소 심각도. 알림 한 건의 심각도 이름과 뜻이 다르다 */
export const RECIPIENT_SEVERITY_LABEL: Record<AlertSeverity, string> = {
  warning: '경고 이상',
  critical: '심각만',
};

/** 규칙 파일 편집기는 화면을 오래 차지하므로 평소에는 접어 둔다 */
export const RULE_EDITOR_TOGGLE_LABEL = {
  expand: '편집기 펼치기',
  collapse: '편집기 접기',
} as const;

/**
 * 규칙이 보는 값의 이름. 계약의 `ALERT_SIGNAL_LABEL`을 화면 말로 바꾼 것이다.
 * 계약 쪽은 규칙 파일(`alerts.yml`)을 쓰는 사람이 보는 이름이라 그대로 둔다.
 */
export const SIGNAL_LABEL: Record<AlertSignal, string> = {
  loop_p99: '처리 지연(상위 1%)',
  heap_used_pct: '메모리 사용률',
  pool_waiting: 'DB 커넥션 풀 대기',
  tx_error_rate: '오류율',
  tx_p99: '응답시간(상위 1%)',
  sql_error: 'SQL 오류 요청',
  db_blocked: 'DB 막힘',
  db_idle_in_tx: '유휴 트랜잭션',
  db_long_query: '긴 쿼리',
  profile_captured: 'CPU 기록 수집',
  instance_gone: '인스턴스 사라짐',
  instance_restarted: '인스턴스 재기동',
  server_error: '관제 서버 오류',
};

// ---- DB 감시 ----

/** 계약의 `DB_EVENT_LABEL`을 화면 말로 */
export const DB_EVENT_LABEL: Record<DbEventKind, string> = {
  lock_wait: '잠금 대기',
  blocked: '막힘',
  idle_in_transaction: '유휴 트랜잭션',
  long_query: '긴 쿼리',
};

/** DB 세션 한 줄이 왜 눈에 띄는지 */
export const DB_SESSION_LABEL = {
  blocked: '막힘',
  blocker: '막는 중',
  waiting: '대기',
  'idle-tx': '유휴 트랜잭션',
  normal: '',
} as const;

// ---- 저장소 ----

/** 계약의 `STORAGE_KIND_LABEL`을 화면 말로 */
export const STORAGE_KIND_LABEL: Record<StorageKind, string> = {
  tx: '요청 요약',
  span: '요청 구간',
  dbevent: 'DB 사건',
  alert: '알림',
  workeragg: '정기 작업 집계',
};

/** 저장 층. 파일 형식 이름은 층 이름으로 쓰지 않고 설명·툴팁에만 둔다 */
export const STORAGE_TIER_LABEL = {
  hot: '최근 저장',
  cold: '장기 보관',
} as const;

// ---- 요청 구간 ----

/** `SpanKindLabel`의 화면 이름. `span`은 "구간"으로 부른다 */
export const SPAN_KIND_LABEL = {
  root: '요청 전체',
  sql: 'SQL',
  ext: '외부 호출',
  internal: '내부 처리',
} as const;

/** 진행 중인 요청이 지금 무엇을 하는 중인지 */
export const ACTIVE_STATE_LABEL = {
  running: '실행 중',
  sql: 'SQL 대기',
  ext: '외부 호출 중',
  waiting: '대기',
} as const;

// ---- 어려운 말에 붙이는 한 줄 설명 ----

/**
 * 그래도 남는 말에만 붙인다. 대부분은 아예 쉬운 말로 바꾸는 것이 낫고,
 * 툴팁은 "왜 이 숫자를 보는가"를 말할 때만 값이 있다.
 */
/**
 * 두 문장이 넘거나 합쳐 40자가 넘는 설명은 문장마다 줄을 나눈다 (23-A회차, UI 피드백 13).
 * 도움말은 `title` 속성으로도 나가므로 줄바꿈은 `\n`이다. 화면에 글자로 나가는 자리는
 * `whitespace-pre-line`을 함께 준다.
 */
export const TIP = {
  loopDelay:
    '앱이 다음 일을 시작하기까지 밀린 시간.\n1초에 100번 재서 느린 쪽 1%를 본다.\n이 값이 커지면 요청 전체가 같이 느려진다',
  loopMax: '같은 1초 동안 가장 크게 밀린 값',
  heap: '앱이 지금 쓰고 있는 메모리와 쓸 수 있는 상한. 비율은 상한 대비다.\n계속 차오르면 어딘가에서 붙잡고 놓지 않는 것이다.\n"(확보한 메모리 기준)"이 붙으면 옛 에이전트라 상한을 몰라 예전 방식(늘 80~97%로 보인다)으로 잰 것이다',
  gcCount: '메모리를 정리한 횟수와 그동안 앱이 멈춰 있던 시간',
  rss: '이 프로세스가 운영체제에서 받아 쓰는 메모리 전체',
  pool: '앱에서 관측한 DB 연결의 사용 중/전체 수와 대기 수.\n값 없음은 지표 미수신, 0/0은 관측된 연결이 0개라는 뜻이다.\nDB 세션 감시의 설정 여부와는 별개다. 대기가 있으면 풀이 부족할 수 있다',
  dotnetThreadPool:
    '.NET 앱이 요청을 처리하는 데 쓰는 스레드 수.\n가용이 0에 붙으면 요청이 스레드를 기다리며 밀린다',
  cpuPercent: '이 프로세스가 쓰고 있는 CPU. 코어 하나를 다 쓰면 100%다',
  completed: '직전 1초에 끝난 요청 수',
  entry: '요청 경로. 웹 요청은 "메서드 + 경로", 정기 작업은 작업 이름이다',
  tenant: '어느 회사의 데이터를 다루는 요청인지.\nOneWorks는 회사별로 데이터가 나뉘어 있다',
  instance: '앱을 여러 개 띄우면 하나하나가 인스턴스(프로세스)다.\n어느 인스턴스에서 났는지',
  traceId: '요청 한 건에 붙는 ID. 앱 로그·DB 세션과 이 값으로 이어 본다',
  txId: '요청 한 건의 실행 ID. 같은 값이 두 번 나오지 않는다',
  p50: '절반의 요청이 이 시간 안에 끝났다',
  p95: '느린 쪽 5%가 이 시간을 넘겼다. 평균보다 체감에 가깝다',
  sqlErrorCode: 'DB가 돌려준 오류 코드. 예를 들어 23505는 유일 제약 위반이다',
  dbTraceComment: 'SQL 앞의 주석으로 어느 요청이 보낸 쿼리인지 알아낸다',
  matchRate: 'DB에서 본 쿼리 중 보낸 요청을 알아낸 비율',
  storageHot: '방금 받은 데이터. 작은 파일 여러 개로 쌓인다(NDJSON)',
  storageCold: '날짜가 끝난 뒤 하나로 변환해 둔 데이터.\n훨씬 작고 조회가 빠르다(Parquet)',
  storageFlush: '받은 데이터를 파일로 내리는 일. 몇 초에 한 번 돈다',
  externalTarget:
    '앱이 밖으로 부른 상대(호스트와 포트).\n같은 상대의 여러 경로는 한 줄로 묶어 센다.\n앱이 자기 자신을 다시 부르면 127.0.0.1로 보인다',
  coveredFrom:
    '요청 구간은 요약보다 보존 기간이 짧다.\n요청한 기간보다 늦은 시각부터만 데이터가 있을 수 있다',
  sseExcluded:
    '실시간 연결은 한 번 붙으면 수십 분 유지된다.\n같이 세면 평균과 건수가 흔들려 기본으로 뺀다',
  workerAggregate:
    '정기 작업은 1분 단위로 집계해 저장한다.\n3초마다 도는 작업이라 한 건씩 남기면 데이터만 커진다',
  fromMemory: '아직 파일로 저장되기 전이라 서버 메모리에 있는 값으로 응답했다',
  detailTruncated:
    '조회 파일 수 상한에 걸려 저장 파일 일부를 못 읽었다. 구간·SQL·이어진 요청이 빠져 있을 수 있다',
  truncated:
    '조회 상한(한 번에 여는 날짜 수·파일 수)에 걸려 일부를 못 봤다.\n표의 숫자가 실제보다 작다. 기간을 좁혀야 전부 보인다',
  userAgent: '요청을 보낸 브라우저·프로그램이 밝힌 이름(User-Agent 헤더)',
  referer:
    '이 요청을 보낸 화면의 주소(Referer 헤더). 어느 화면에서 눌렀는지 알 수 있다.\n주소에 있던 서명 토큰은 지운 값이다',
  responseBytes:
    '앱이 돌려준 응답 본문의 크기.\n응답 헤더(content-length)에 적힌 값이라 흘려보내는 응답에는 없다',
  processStarted:
    '이 프로세스가 뜬 시각. 배포나 재시작이 있었으면 이 시각이 바뀐다.\n옆은 붙어 있는 에이전트 버전',
  sqlTruncated:
    'SQL 문장 하나를 보낼 때 길이 상한이 있다.\n주석이 많은 긴 문장은 뒤가 잘려 저장된다.\n전체 문장은 앱 소스나 DB 로그에서 봐야 한다',
  errorFrames:
    '오류가 어느 줄에서 났는지.\n서버 오류(500번대)인 요청에만 앞 몇 줄을 보관한다.\n라이브러리 안의 줄은 빼고 앱 코드를 먼저 보인다.\n전체 스택은 앱 로그에서 봐야 한다',
  selfQueue:
    '대상 앱이 보낸 것을 잠시 담아 두는 자리.\n여기가 차면 오래된 것부터 버린다.\n버린 수가 0이 아니면 관제 서버가 받는 속도를 못 따라간 것이다',
  selfNormalize: '받은 조각을 요청 한 건으로 묶는 일.\n요청 전체가 끝내 안 오면 그 요청은 버린다',
  selfWriter: '받은 것을 파일로 내리는 자리.\n실패 수가 0이 아니면 디스크나 권한을 봐야 한다',
  selfQueryLane: '조회와 장기 보관 변환은 줄을 따로 선다.\n줄이 상한까지 차면 새 조회를 거절한다',
  selfLog:
    '관제 서버가 남긴 경고·오류 중 최근 것.\n프로세스가 다시 뜨면 사라진다(원본은 서버 로그에 있다)',
} as const;

// ---- 로그인 (51회차) ----

export const AUTH_TEXT = {
  title: '로그인',
  note: '계정은 관제 서버의 계정 파일(APM_AUTH_USERS)이나 설정(APM_AUTH_USER·APM_AUTH_PASSWORD)에 있다',
  username: '아이디',
  password: '비밀번호',
  submit: '로그인',
  submitting: '확인 중',
  checking: '로그인 확인 중',
  recheck: '다시 확인',
  relogout: '다시 로그아웃',
  logoutFailed:
    '서버에 로그아웃을 알리지 못했다. 이 브라우저에 로그인이 남아 있을 수 있으니 다시 로그아웃을 누를 것',
  logout: '로그아웃',
  failed: '로그인하지 못했다',
  checkFailed: '로그인 상태를 확인하지 못했다',
} as const;

/** 계정마다 볼 수 있는 범위 (52회차) */
export const ACCESS_TEXT = {
  adminOnlyTitle: '관리자만 볼 수 있는 화면이다',
  adminOnlyHint:
    '이 계정으로는 열 수 없다. 화면에서 감춘 것과 별개로 관제 서버도 이 조회를 거절한다(403). 필요하면 관리자 계정으로 로그인할 것',
  foreignService: (service: string) =>
    `이 계정이 볼 수 없는 서비스라 서비스 목록으로 옮겼다: ${service}`,
  adminOnlyStatus: '관리자만 본다',
  dismiss: '닫기',
} as const;

// ---- 자주 쓰는 문장 ----

export const MSG = {
  loading: '읽는 중',
  reload: '새로고침',
  noAgent: '보고 중인 인스턴스가 없다',
  noAgentHint: '대상 앱에 에이전트가 붙어 있는지, 관제 서버 주소(APM_ENDPOINT)가 맞는지 확인할 것',
  noTraffic: '이 기간에 완료된 요청이 없다',
  narrowRange: '기간을 좁혀서 다시 볼 것',
  /**
   * 메모리 사용률의 분모가 상한이 아니라 확보한 양일 때 붙는 말 (44회차). 옛 에이전트가 상한을 안
   * 보낼 때다. 43회차까지는 인스턴스 카드에 "힙"이 그대로 적혀 있었다
   */
  heapBasisTotal: '(확보한 메모리 기준)',
} as const;
